// frontend/src/admin/v2/components/RecentFailuresCard.jsx
import { Stack, Typography, Box, Skeleton } from '@mui/material'
import Panel from '../../../components/common/Panel'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import { SP, FS } from '../../../theme/tokens'
import { formatBerlinDate } from '../../../utils/time'

/**
 * RecentFailuresCard — list of the last 10 errored sync-runs in the past 24h.
 *
 * @description
 *   Renders a clean line per failure with relative time + monospaced kind +
 *   error snippet. Clamps snippet at 2 lines.
 *
 *   🔴 DREI Zustände, nicht zwei (Regel 22 §3, „Unknown is never `0`"):
 *   - `loading`               → Skelett.
 *   - `failures == null`      → „Failures could not be loaded." in `body2`,
 *                               `text.secondary`, OHNE Haken und ohne Icon. Das
 *                               ist der UNBEKANNTE Stand (gescheiterter Abruf) —
 *                               die Ausfallmeldung selbst steht im Alert darüber.
 *   - `failures.length === 0` → Haken und „No failures in the last 24 hours."
 *                               Das ist der GEMESSENE leere Stand und bleibt der
 *                               einzige Weg zu dieser Aussage.
 *
 * @props
 *   failures: [{id, kind, status, started_at, finished_at, error_snippet}] | null
 *             — `null` heisst „unbekannt" (kein Abruf), `[]` heisst „gemessen leer"
 *   loading: boolean — render skeleton placeholders if true
 */
function formatTime(iso) {
  if (!iso) return '—'
  const d = new Date(iso)
  const diffMin = Math.round((Date.now() - d.getTime()) / 60_000)
  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `${diffH}h ago`
  return formatBerlinDate(d)
}

export default function RecentFailuresCard({ failures, loading }) {
  return (
    <Panel>
      <Stack spacing={SP.card}>
        <Typography variant="subtitle2" sx={{ fontSize: FS.sm, fontWeight: 600 }}>
          Recent failures · last 24h
        </Typography>

        {loading ? (
          <Stack spacing={SP.tight}>
            {[0, 1, 2].map(i => <Skeleton key={i} variant="text" height={28} />)}
          </Stack>
        ) : failures == null ? (
          // UNBEKANNT: der Abruf ist gescheitert. Kein Haken, kein Icon, keine
          // Aussage über den Bestand — nur die Feststellung, dass sie fehlt.
          <Typography variant="body2" color="text.secondary" sx={{ py: SP.card }}>
            Failures could not be loaded.
          </Typography>
        ) : failures.length === 0 ? (
          <Stack direction="row" spacing={SP.tight} alignItems="center" sx={{ py: SP.card }}>
            <CheckCircleOutlineIcon sx={{ color: 'success.main', fontSize: 20 }} />
            <Typography variant="body2" color="text.secondary">
              No failures in the last 24 hours.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={SP.tight} divider={<Box sx={{ borderBottom: 1, borderColor: 'divider' }} />}>
            {failures.map(f => (
              <Stack key={f.id} direction="row" spacing={SP.tight} alignItems="flex-start" sx={{ pb: 0.5 }}>
                <ErrorOutlineIcon sx={{ color: 'error.main', fontSize: 16, mt: 0.25, flexShrink: 0 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={SP.tight} alignItems="baseline" flexWrap="wrap">
                    <Typography sx={{ fontSize: FS.sm, fontWeight: 500, fontFamily: 'monospace' }}>
                      {f.kind}
                    </Typography>
                    <Typography variant="caption" sx={{ fontSize: FS.xs, color: 'text.secondary' }}>
                      {formatTime(f.started_at)}
                    </Typography>
                  </Stack>
                  {f.error_snippet && (
                    <Typography
                      sx={{
                        fontSize: FS.xs,
                        color: 'text.secondary',
                        mt: 0.25,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                      }}
                    >
                      {f.error_snippet}
                    </Typography>
                  )}
                </Box>
              </Stack>
            ))}
          </Stack>
        )}
      </Stack>
    </Panel>
  )
}
